import React, { useState, useMemo, useEffect } from 'react'
import { Search, Plus, Calendar, CheckCircle, Clock, AlertCircle, Home, FileText, Settings, BarChart3, Users, Bell, Upload, Globe, Copy, ExternalLink, ArrowLeft, CloudUpload, Trash2, LogOut, Loader2, X as XIcon, Menu, ChevronUp, ChevronDown } from 'lucide-react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Button } from "./components/ui/Button"
import { Input } from "./components/ui/Input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/Card"
import { Badge } from "./components/ui/Badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/Table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/Select"
import { Avatar, AvatarFallback, AvatarImage } from "./components/ui/Avatar"
import { Separator } from "./components/ui/Separator"
import { Textarea } from "./components/ui/Textarea"
import { Label } from "./components/ui/Label"
import { Switch } from "./components/ui/Switch"
import { uploadFileToSupabase } from './lib/uploadFileToSupabase';
import { createReviewRequest } from './lib/createReviewRequest';
import { convertWithCloudConvert } from './lib/cloudConvert';
import { useUser, useAuth, useClerk } from '@clerk/clerk-react';
import { Routes, Route, useParams, useNavigate, useLocation } from 'react-router-dom';
import LoginPage from './LoginPage';
import SignUpPage from './SignUpPage';
import SettingsPage from './SettingsPage';
import ReviewPage from './reviewpage';
import { sendEmailNotification, EmailService } from './lib/sendEmailNotification';
import { customAlphabet } from 'nanoid';
import { supabase } from './lib/supabaseClient';
import mobileLogo from './assets/logos/LogoA/Untitled-1@0.25x.png';
import MainShell from './components/MainShell';
import PlaceholderPage from './pages/PlaceholderPage';
import ReviewTable from './components/ReviewTable';
import SubscriptionTest from './components/SubscriptionTest';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import { AppLayout } from './components/AppLayout';
import { useUserProfile } from './hooks/useUserProfile';
import { useAnalytics } from './hooks/useAnalytics';


interface ReviewRequest {
  id: string
  title: string
  status: 'open' | 'changes-needed' | 'approved'
  dueDate: string
  requester: {
    name: string
    avatar?: string
  }
  createdAt: string
}

interface SidebarProps {
  activeItem?: string;
  onMenuClick?: (itemId: string) => void;
  mobile?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ activeItem = 'dashboard', onMenuClick, mobile = false }) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { signOut } = useClerk();

  const signOutAndRedirect = async () => {
    try {
      await signOut();
    } finally {
      navigate('/login');
    }
  }
  const menuItems = [
    { id: 'start', label: 'Create', icon: Plus },
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'reviews', label: 'Reviews', icon: FileText },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'settings', label: 'Settings', icon: Settings },
  ]

  return (
    <div className={`${mobile ? 'flex' : 'hidden md:flex'} w-64 h-screen bg-background border-r border-border flex-col`}>
      <div className="p-6">
        <div className="flex items-center">
          <img src="/approvia-logo.png" alt="Approvia Logo" className="w-32 h-auto object-contain" />
        </div>
      </div>
      
      <nav className="flex-1 px-4">
        <ul className="space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon
            const isActive = item.id === activeItem
            return (
              <li key={item.id}>
                <button
                  onClick={() => {
                    if(item.id!==activeItem){
                      onMenuClick?.(item.id);
                    }
                  }}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </button>
              </li>
            )
          })}
        </ul>
      </nav>

      <div className="p-4 border-t border-border">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
            >
          <Avatar className="w-8 h-8">
                <AvatarImage src={user?.imageUrl || '/placeholder-avatar.jpg'} />
                <AvatarFallback>
                  {user ? (
                    (user.firstName?.[0] || '') + (user.lastName?.[0] || '')
                  ) : (
                    'JD'
                  )}
                </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.fullName || 'John Doe'}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.primaryEmailAddress?.emailAddress || 'john@company.com'}</p>
          </div>
        </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content side="top" align="start" className="z-50 min-w-[200px] bg-background border border-border rounded-md shadow-lg p-1">
              <DropdownMenu.Item
                className="flex items-center space-x-2 px-2 py-2 rounded-md text-sm cursor-pointer text-foreground hover:bg-accent hover:text-accent-foreground"
                onSelect={() => navigate('/settings')}
              >
                <Settings className="h-4 w-4" />
                <span>Settings</span>
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className="flex items-center space-x-2 px-2 py-2 rounded-md text-sm cursor-pointer text-foreground hover:bg-destructive/90 hover:text-destructive-foreground"
                onSelect={() => signOutAndRedirect()}
              >
                <LogOut className="h-4 w-4" />
                <span>Log out</span>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </div>
  )
}

export { Sidebar };

// Upload Flow Component
const UploadFlow: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [step, setStep] = useState(1)
  const [uploadType, setUploadType] = useState<'document' | 'website' | null>(null)
  const [files, setFiles] = useState<File[]>([])
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const { user } = useUser();
  const { trackReviewCreated, trackFileUploaded, trackError, trackButtonClick, trackEvent } = useAnalytics();


  const [formData, setFormData] = useState({
    userEmail: user?.primaryEmailAddress?.emailAddress || '',
    reviewerEmail: '',
    reviewerFirstName: '',
    reviewerLastName: '',
    reviewTitle: '',
    deadlineEnabled: false,
    deadline: '',
    deadlineTime: '',
    reminderEnabled: false,
    reminders: [] as Array<{ type: string; customDate?: string; customTime?: string }>,
    comment: '',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  })
  const [generatedLink, setGeneratedLink] = useState('')
  const { getToken } = useAuth();
  const [formErrors, setFormErrors] = useState<{ reviewerEmail?: string; reviewerFirstName?: string; reviewerLastName?: string; deadline?: string; reminders?: string }>({});

  // Helper function to get current date in YYYY-MM-DD format
  const getCurrentDate = () => new Date().toISOString().split('T')[0];
  
  // Helper function to get current time in HH:MM format
  const getCurrentTime = () => {
    const now = new Date();
    return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
  };

  const SUPPORTED_FILE_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'image/jpeg',
    'image/png',
    'image/gif',
  ];

  const handleFileUpload = async (uploadedFiles: FileList) => {
    // Filter out unsupported files
    const fileArray = Array.from(uploadedFiles).filter(file => SUPPORTED_FILE_TYPES.includes(file.type));
    setFiles(fileArray);
    
    // Track file upload
    if (fileArray.length > 0) {
      await trackFileUploaded(
        fileArray[0].type,
        fileArray[0].size,
        {
          file_name: fileArray[0].name,
          upload_type: 'document',
          step: 'file_selection'
        }
      );
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const droppedFiles = e.dataTransfer.files
    handleFileUpload(droppedFiles)
  }

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index))
  }

  // Helper to derive a sensible default title from a file name or URL
  const deriveTitle = () => {
    if (uploadType === 'document' && files.length > 0) {
      return files[0].name.replace(/\.[^.]+$/, '');
    }
    if (uploadType === 'website' && websiteUrl.trim()) {
      try {
        return new URL(websiteUrl.trim()).hostname;
      } catch {
        return websiteUrl.trim();
      }
    }
    return '';
  };

  const handleContinueToStep2 = async () => {
    // Track button click
    await trackButtonClick('continue_to_step_2', 'upload_flow', {
      upload_type: uploadType,
      has_files: files.length > 0,
      has_website_url: websiteUrl.trim().length > 0
    });
    
    // Track step completion
    trackEvent('Upload Flow', 'step_completed', 'step_1', 1);
    
    // Prefill title if empty
    setFormData(prev => ({ ...prev, reviewTitle: prev.reviewTitle || deriveTitle() }));
    setStep(2)
  }

  // Helper to generate an 8-character review slug
  const nanoid = useMemo(() => customAlphabet('0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz', 8), []);

  function generateReviewLink() {
    return nanoid();
  }

  const handleSubmitForm = async () => {
    // 1. Ensure the user is logged in first so we have a valid Clerk user ID
    if (!user) {
      alert('You must be logged in to upload.');
      return;
    }

    // 2. Retrieve the logged-in user email (primary address)
    const userEmail = user.primaryEmailAddress?.emailAddress || '';

    // If the email is missing (should not normally happen) show a clear error instead of failing silently
    if (!userEmail) {
      alert('Could not determine your e-mail address. Please make sure your Clerk profile has a primary email.');
      return;
    }

    // Persist the email into the form state so it is displayed in the UI and sent to the backend
    formData.userEmail = userEmail;
    // Validate reviewer email (required)
    const errors: { reviewerEmail?: string; deadline?: string; reminders?: string } = {};
    if (!formData.reviewerEmail.trim()) {
      errors.reviewerEmail = 'Reviewer Email is required.';
    }
    
    // Validate deadline is not in the past
    if (formData.deadlineEnabled && formData.deadline) {
      const deadlineDate = new Date(`${formData.deadline}T${formData.deadlineTime || '23:59:59'}`);
      const now = new Date();
      if (deadlineDate <= now) {
        errors.deadline = 'Deadline cannot be in the past. Please select a future date and time.';
      }
    }
    
    // Validate reminders are not in the past
    if (formData.reminders.length > 0) {
      for (const reminder of formData.reminders) {
        if (reminder.type === 'custom' && reminder.customDate && reminder.customTime) {
          const reminderDate = new Date(`${reminder.customDate}T${reminder.customTime}`);
          const now = new Date();
          if (reminderDate <= now) {
            errors.reminders = 'Custom reminders cannot be in the past. Please select a future date and time.';
            break;
          }
        }
      }
    }
    
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    // Compose deadline as UTC ISO string and store time zone
    let deadlineUtc: string | null = null;
    if (formData.deadlineEnabled && formData.deadline) {
      let deadlineString = formData.deadline;
      let timeString = formData.deadlineTime;
      if (!timeString) {
        // If no time is provided, default to 23:59:59 local time and warn
        timeString = '23:59:59';
        alert('No deadline time provided. Defaulting to 23:59:59 (end of day).');
      }
      const localDate = new Date(`${deadlineString}T${timeString}`);
      deadlineUtc = localDate.toISOString();
    }
    setIsUploading(true);
    try {
      let review: any;
      const reviewLink = generateReviewLink();
      // Helper to ensure website URL has protocol
      const normalizeUrl = (url: string) => {
        if (!/^https?:\/\//i.test(url)) {
          return `https://${url}`;
        }
        return url;
      };

      if (uploadType === 'document' && files.length > 0) {
        try {
          console.log('Starting file upload process for:', files[0].name);
          const fileUrl = await uploadFileToSupabase(files[0], user.id);
          console.log('Initial file upload successful:', fileUrl);
          
          let finalFileUrl = fileUrl;
          const isPdfOrImage = files[0].type.startsWith('image/') || files[0].type === 'application/pdf';
          
          if (!isPdfOrImage) {
            console.log('File is not PDF or image, converting with CloudConvert...');
            const accessToken = await getToken();
            if (!accessToken) {
              alert('Could not get access token.');
              setIsUploading(false);
              return;
            }
            // 1. Convert with CloudConvert (returns exportUrl)
            const exportUrl = await convertWithCloudConvert({
              fileUrl,
              fileName: files[0].name,
              convertTo: 'pdf',
              accessToken,
            });
            console.log('CloudConvert successful, export URL:', exportUrl);
            
            // 2. Download the file from exportUrl
            const response = await fetch(exportUrl);
            if (!response.ok) {
              throw new Error(`Failed to download converted file: ${response.status} ${response.statusText}`);
            }
            const blob = await response.blob();
            console.log('Downloaded converted file, size:', blob.size);
            
            // 3. Create a File object for Supabase upload
            const convertedFile = new File([blob], files[0].name.replace(/\.[^.]+$/, '.pdf'), { type: 'application/pdf' });
            // 4. Upload to Supabase Storage
            finalFileUrl = await uploadFileToSupabase(convertedFile, user.id);
            console.log('Converted file upload successful:', finalFileUrl);
          }
          
          console.log('Creating review request with file URL:', finalFileUrl);
          review = await createReviewRequest({
            userId: user.id,
            type: 'document',
            fileUrl: finalFileUrl,
            reviewLink,
            fileType: files[0].type,
            deadline: deadlineUtc,
            timeZone: formData.timeZone,
            reviewerFirstName: formData.reviewerFirstName,
            reviewerLastName: formData.reviewerLastName,
            reviewerEmail: formData.reviewerEmail,
            title: formData.reviewTitle || deriveTitle(),
          });
          console.log('Review request created successfully:', review);
        } catch (uploadError: any) {
          console.error('File upload error:', uploadError);
          throw new Error(`File upload failed: ${uploadError.message}`);
        }
      } else if (uploadType === 'website' && websiteUrl.trim()) {
        const normalizedWebsiteUrl = normalizeUrl(websiteUrl.trim());
        let finalScreenshotUrl: string = normalizedWebsiteUrl; // fallback if everything fails
        try {
          finalScreenshotUrl = await convertWithCloudConvert({
            websiteUrl: normalizedWebsiteUrl,
          fileName: 'website-screenshot',
          convertTo: 'jpg',
        });
        } catch (err) {
          console.warn('CloudConvert failed, falling back to raw URL', err);
        }
        review = await createReviewRequest({
          userId: user.id,
          type: 'website',
          websiteUrl: normalizedWebsiteUrl,
          fileUrl: finalScreenshotUrl, // ensure NOT NULL
          reviewLink,
          fileType: 'image/jpeg',
          deadline: deadlineUtc,
          timeZone: formData.timeZone,
          reviewerFirstName: formData.reviewerFirstName,
          reviewerLastName: formData.reviewerLastName,
          reviewerEmail: formData.reviewerEmail,
          title: formData.reviewTitle || deriveTitle(),
        });
      }
      if (review) {
        // Use the unique review_link from the review object
        const baseUrl = window.location.origin;
        const reviewUrl = `${baseUrl}/review/${review.review_link}`;
        // do not set generatedLink yet – will set personalised link below (or fall back)
        let finalLink = reviewUrl;
        // Insert reviewer row and send email
        if (formData.reviewerEmail) {
          try {
            const { createReviewer } = await import('./lib/createReviewer');
            const reviewerRow = await createReviewer({
              reviewRequestId: review.id,
              reviewerEmail: formData.reviewerEmail,
              reviewerFirstName: formData.reviewerFirstName,
              reviewerLastName: formData.reviewerLastName,
            });
            const personalisedLink = `${baseUrl}/review/${review.review_link}?id=${reviewerRow.reviewer_link}`;
            finalLink = personalisedLink;

            // Fire email send asynchronously; don't block UI
            (async () => {
              try {
                console.log('Sending review assignment email to:', formData.reviewerEmail);
                console.log('Review data:', { review_link: review.review_link, title: review.title });
                
                // Get creator's profile information
                let creatorName = '';
                let creatorEmail = '';
                try {
                  const { SubscriptionService } = await import('./lib/subscriptionService');
                  const userProfile = await SubscriptionService.getUserProfile(user.id);
                  if (userProfile) {
                    creatorName = `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim();
                    creatorEmail = userProfile.email || '';
                  }
                } catch (profileError) {
                  console.warn('Could not fetch creator profile:', profileError);
                  // Fallback to user data from Clerk
                  creatorName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
                  creatorEmail = user.primaryEmailAddress?.emailAddress || '';
                }
                
                console.log('Creator info:', { creatorName, creatorEmail });
                
                const emailVariables = {
                  reviewerName: `${formData.reviewerFirstName} ${formData.reviewerLastName}`.trim() || formData.reviewerEmail,
                  reviewUrl: personalisedLink,
                  reviewTitle: review.title,
                  creatorName: creatorName || 'Unknown User',
                  creatorEmail: creatorEmail,
                  deadline: formData.deadlineEnabled ? `${formData.deadline} ${formData.deadlineTime}` : undefined,
                  comment: formData.comment,
                };
                
                console.log('Email variables:', emailVariables);
                
                const result = await sendEmailNotification({
                  to: formData.reviewerEmail,
                  templateKey: 'review-assigned',
                  variables: emailVariables,
                });
                console.log('Email sent successfully:', result);
              } catch (e) {
                console.error('Failed to send email notification:', e);
                // Show user-friendly error message
                alert(`Review created successfully, but email notification failed: ${e}`);
              }
            })();
          } catch (e) {
            console.error('Failed to create reviewer row', e);
          }
        }
        
        // Track successful review creation
        await trackReviewCreated({
          review_id: review.id,
          review_type: uploadType,
          has_deadline: formData.deadlineEnabled,
          has_reminders: formData.reminders.length > 0,
          reviewer_count: formData.reviewerEmail ? 1 : 0,
          step: 'review_created'
        });
        
        // Track review creation event
        trackEvent('Review', 'created', uploadType || 'unknown', 1);
        
        setGeneratedLink(finalLink);
        setStep(3);

        // Insert reminders if any
        if (formData.reminders.length > 0) {
          console.log('Processing reminders:', formData.reminders);
          const reminderRows = formData.reminders.map((r) => {
            let remindAt: Date | null = null;
            if (r.type === '3-days' || r.type === '1-day' || r.type === '3-hours') {
              if (!formData.deadline || !formData.deadlineTime) {
                console.log('Skipping reminder - no deadline set');
                return null;
              }
              const deadlineDateTime = new Date(`${formData.deadline}T${formData.deadlineTime}`);
              remindAt = new Date(deadlineDateTime);
              switch (r.type) {
                case '3-days': remindAt.setDate(deadlineDateTime.getDate() - 3); break;
                case '1-day': remindAt.setDate(deadlineDateTime.getDate() - 1); break;
                case '3-hours': remindAt.setHours(deadlineDateTime.getHours() - 3); break;
              }
            } else if (r.type === 'custom' && r.customDate && r.customTime) {
              remindAt = new Date(`${r.customDate}T${r.customTime}`);
            } else {
              console.log('Skipping reminder - invalid type or missing data:', r);
              return null;
            }
            return remindAt ? { review_request_id: review.id, remind_at: remindAt.toISOString() } : null;
          }).filter(Boolean);
          console.log('Reminder rows to insert:', reminderRows);
          if (reminderRows.length > 0) {
            const { error: reminderError } = await supabase.from('review_reminders').insert(reminderRows as any);
            if (reminderError) {
              console.error('Failed to insert reminders:', reminderError);
              // Don't fail the entire operation for reminder errors, but log them
            } else {
              console.log('Successfully inserted reminders');
            }
          } else {
            console.log('No valid reminders to insert');
          }
        } else {
          console.log('No reminders configured');
        }
      }
    } catch (err: any) {
      const errorMessage = err?.message || String(err);
      alert('Failed to create review: ' + errorMessage);
      
      // Track error
      await trackError('review_creation_error', errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedLink)
  }

  const openInNewWindow = () => {
    window.open(generatedLink, '_blank')
  }

  // Step 1: Upload Type Selection
  if (step === 1) {
    return (
      <div className="max-w-4xl mx-auto p-4 md:p-6">
        <div className="flex items-center mb-6">
          <Button variant="ghost" onClick={onBack} className="mr-2 md:mr-4">
            <ArrowLeft className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Back</span>
          </Button>
          <h1 className="text-xl md:text-2xl font-bold">Create New Review Request</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
          <Card 
            className={`cursor-pointer transition-all hover:shadow-md ${uploadType === 'document' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setUploadType('document')}
          >
            <CardHeader className="text-center p-4 md:p-6">
              <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <Upload className="w-6 h-6 md:w-8 md:h-8 text-blue-600" />
              </div>
              <CardTitle className="text-lg md:text-xl">Upload Documents</CardTitle>
              <CardDescription className="text-sm md:text-base">
                Upload Word, PowerPoint, PDF, or Image files for review
              </CardDescription>
            </CardHeader>
          </Card>

          <Card 
            className={`cursor-pointer transition-all hover:shadow-md ${uploadType === 'website' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setUploadType('website')}
          >
            <CardHeader className="text-center p-4 md:p-6">
              <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <Globe className="w-6 h-6 md:w-8 md:h-8 text-green-600" />
              </div>
              <CardTitle className="text-lg md:text-xl">Review Website</CardTitle>
              <CardDescription className="text-sm md:text-base">
                Enter a website URL for review and feedback
              </CardDescription>
            </CardHeader>
          </Card>
        </div>

        {/* User Instructions */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-medium">i</span>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-blue-900 mb-1">How to create a review request</h3>
              <div className="text-sm text-blue-700 space-y-1">
                <p>1. <strong>Choose your content type</strong> - Upload documents or enter a website URL</p>
                <p>2. <strong>Add review details</strong> - Enter reviewer information, title, deadline, and optional reminders</p>
                <p>3. <strong>Get your review link</strong> - Share the generated link with your reviewer</p>
              </div>
            </div>
          </div>
        </div>

        {uploadType === 'document' && (
          <Card>
            <CardHeader>
              <CardTitle>Upload Documents</CardTitle>
              <CardDescription>Drag and drop files or click to browse</CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 md:p-8 text-center hover:border-gray-400 transition-colors cursor-pointer"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => document.getElementById('file-input')?.click()}
              >
                <CloudUpload className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 md:mb-4 text-gray-400" />
                <p className="text-base md:text-lg mb-2">Drop files here or click to browse</p>
                <p className="text-xs md:text-sm text-gray-500">Supports: Word, PowerPoint, PDF, Images</p>
                <input
                  id="file-input"
                  type="file"
                  multiple
                  accept=".doc,.docx,.ppt,.pptx,.pdf,.jpg,.jpeg,.png,.gif"
                  onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                  className="hidden"
                />
              </div>

              {files.length > 0 && (
                <div className="mt-4 space-y-2">
                  <h4 className="font-medium">Selected Files:</h4>
                  {files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                      <span className="text-sm">{file.name}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end mt-6">
                <Button 
                  onClick={handleContinueToStep2}
                  disabled={files.length === 0 || isUploading}
                >
                  {isUploading ? 'Uploading...' : 'Continue'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {uploadType === 'website' && (
          <Card>
            <CardHeader>
              <CardTitle>Website URL</CardTitle>
              <CardDescription>Enter the website URL you want to review</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="website-url">Website URL</Label>
                  <Input
                    id="website-url"
                    type="url"
                    placeholder="https://example.com"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                  />
                </div>
                <div className="flex justify-end">
                  <Button 
                    onClick={handleContinueToStep2}
                    disabled={!websiteUrl.trim() || isUploading}
                  >
                    {isUploading ? 'Processing...' : 'Continue'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    )
  }

  // Step 2: Form Details
  if (step === 2) {
    // Helper to get the user's local time zone string
    const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return (
      <div className="max-w-2xl mx-auto p-4 md:p-6">
        <div className="flex items-center mb-6">
          <Button variant="ghost" onClick={() => setStep(1)} className="mr-2 md:mr-4">
            <ArrowLeft className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Back</span>
          </Button>
          <h1 className="text-xl md:text-2xl font-bold">Review Details</h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Setup Review Request</CardTitle>
            <CardDescription>Provide details for your review request</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Review Title - first field */}
            <div>
              <Label htmlFor="review-title">Review Title<span className="text-red-500">*</span></Label>
              <Input
                id="review-title"
                type="text"
                maxLength={120}
                placeholder="e.g. Marketing Brochure Q3"
                value={formData.reviewTitle}
                onChange={(e) => setFormData({ ...formData, reviewTitle: e.target.value })}
                required
              />
            </div>

            {/* Separator between required and optional fields */}
            <Separator className="my-4" />

            {/* Reviewer Email - required */}
            <div>
              <Label htmlFor="reviewer-email">Reviewer Email<span className="text-red-500">*</span></Label>
              <Input
                id="reviewer-email"
                type="email"
                placeholder="reviewer@company.com"
                value={formData.reviewerEmail}
                onChange={(e) => setFormData({...formData, reviewerEmail: e.target.value})}
                required
              />
              {formErrors.reviewerEmail && (
                <div className="text-red-500 text-xs mt-1">{formErrors.reviewerEmail}</div>
              )}
            </div>

            {/* Optional reviewer names */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="reviewer-first-name">Reviewer First Name (optional)</Label>
                <Input
                  id="reviewer-first-name"
                  type="text"
                  placeholder="First Name"
                  value={formData.reviewerFirstName}
                  onChange={(e) => setFormData({...formData, reviewerFirstName: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="reviewer-last-name">Reviewer Last Name (optional)</Label>
                <Input
                  id="reviewer-last-name"
                  type="text"
                  placeholder="Last Name"
                  value={formData.reviewerLastName}
                  onChange={(e) => setFormData({...formData, reviewerLastName: e.target.value})}
                />
              </div>
            </div>

            {/* (Review Title moved above) */}

            {/* Divider before Deadline section */}
            <Separator className="my-4" />

            {/* Deadline Toggle */}
            <div className="flex items-center space-x-2">
              <Switch
                id="deadline-toggle"
                checked={formData.deadlineEnabled}
                onCheckedChange={(checked) => setFormData({...formData, deadlineEnabled: checked})}
              />
              <Label htmlFor="deadline-toggle">Set Deadline</Label>
            </div>

            {formData.deadlineEnabled && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label htmlFor="deadline-date" className="text-sm">
                      Deadline Date
                      <span className="text-xs text-muted-foreground ml-1 block md:inline">
                        ({localTimeZone})
                      </span>
                    </Label>
                    <Input
                      id="deadline-date"
                      type="date"
                      min={getCurrentDate()}
                      value={formData.deadline}
                      onChange={(e) => setFormData({...formData, deadline: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="deadline-time" className="text-sm">
                      Deadline Time
                      <span className="text-xs text-muted-foreground ml-1 block md:inline">
                        ({localTimeZone})
                      </span>
                    </Label>
                    <Input
                      id="deadline-time"
                      type="time"
                      min={formData.deadline === getCurrentDate() ? getCurrentTime() : undefined}
                      value={formData.deadlineTime}
                      onChange={(e) => setFormData({...formData, deadlineTime: e.target.value})}
                    />
                  </div>
                </div>

                {/* Deadline validation message */}
                {(!!formData.deadline !== !!formData.deadlineTime) && (
                  <div className="text-red-500 text-xs mt-1">
                    Please enter both deadline date and time, or leave both empty.
                  </div>
                )}
                
                {/* Deadline past validation */}
                {formErrors.deadline && (
                  <div className="text-red-500 text-xs mt-1">{formErrors.deadline}</div>
                )}
              </>
            )}

            <div className="flex flex-col gap-6">
              {/* Reminder section */}
              <div>
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="reminder-toggle"
                  checked={formData.reminderEnabled}
                  onCheckedChange={(checked) => setFormData({...formData, reminderEnabled: checked})}
                  disabled={!formData.deadlineEnabled || !formData.deadline || !formData.deadlineTime}
                />
                <Label htmlFor="reminder-toggle" className={!formData.deadlineEnabled || !formData.deadline || !formData.deadlineTime ? 'text-muted-foreground' : ''}>
                  Set Reminder
                </Label>
                {(!formData.deadlineEnabled || !formData.deadline || !formData.deadlineTime) && (
                  <span className="text-xs text-muted-foreground ml-2">
                    (Set deadline first)
                  </span>
                )}
              </div>

              {formData.reminderEnabled && formData.deadlineEnabled && formData.deadline && formData.deadlineTime && (
                <div className="space-y-4 pl-6">
                    {formData.reminders.map((rem, idx) => (
                      <div key={idx} className="border rounded-lg p-4 space-y-4 relative">
                        {/* remove button */}
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, reminders: formData.reminders.filter((_, i) => i !== idx) })}
                          className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
                        >
                          <XIcon className="w-4 h-4" />
                        </button>
                        <Label>Reminder Options</Label>
                        <select
                          value={rem.type}
                          onChange={(e) => {
                            const newR = [...formData.reminders];
                            newR[idx].type = e.target.value;
                            setFormData({ ...formData, reminders: newR });
                          }}
                          className="w-full border border-input rounded-md p-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                        >
                          <option value="" disabled>Choose reminder time</option>
                          <option value="3-days">3 days before deadline</option>
                          <option value="1-day">1 day before deadline</option>
                          <option value="3-hours">3 hours before deadline</option>
                          <option value="custom">Custom date & time</option>
                        </select>

                        {rem.type && rem.type !== 'custom' && formData.deadlineEnabled && formData.deadline && formData.deadlineTime && (
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <Label className="text-sm font-medium">Reminder will be sent at:</Label>
                            <p className="text-sm mt-1">
                              {(() => {
                                const deadlineDateTime = new Date(`${formData.deadline}T${formData.deadlineTime}`);
                                let reminderDateTime = new Date(deadlineDateTime);
                                switch (rem.type) {
                                  case '3-days':
                                    reminderDateTime.setDate(deadlineDateTime.getDate() - 3);
                                    break;
                                  case '1-day':
                                    reminderDateTime.setDate(deadlineDateTime.getDate() - 1);
                                    break;
                                  case '3-hours':
                                    reminderDateTime.setHours(deadlineDateTime.getHours() - 3);
                                    break;
                                }
                                return reminderDateTime.toLocaleString('en-US', {
                                  weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short'
                                });
                              })()}
                            </p>
                          </div>
                        )}

                        {rem.type === 'custom' && (
                          <> 
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor={`reminder-date-${idx}`} className="text-sm">Reminder Date</Label>
                                <Input 
                                  id={`reminder-date-${idx}`} 
                                  type="date" 
                                  min={getCurrentDate()}
                                  value={rem.customDate || ''} 
                                  onChange={(e) => {
                                    const newR = [...formData.reminders];
                                    newR[idx].customDate = e.target.value;
                                    setFormData({ ...formData, reminders: newR });
                                  }} 
                                />
                              </div>
                              <div>
                                <Label htmlFor={`reminder-time-${idx}`} className="text-sm">Reminder Time</Label>
                                <Input 
                                  id={`reminder-time-${idx}`} 
                                  type="time" 
                                  min={rem.customDate === getCurrentDate() ? getCurrentTime() : undefined}
                                  value={rem.customTime || ''} 
                                  onChange={(e) => {
                                    const newR = [...formData.reminders];
                                    newR[idx].customTime = e.target.value;
                                    setFormData({ ...formData, reminders: newR });
                                  }} 
                                />
                              </div>
                            </div>
                            {rem.customDate && rem.customTime && (
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <Label className="text-sm font-medium">Reminder will be sent at:</Label>
                                <p className="text-sm mt-1">{new Date(`${rem.customDate}T${rem.customTime}`).toLocaleString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })}</p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}

                    {formData.reminders.length < 3 && (
                      <Button type="button" variant="outline" size="sm" className="mt-2 gap-1" onClick={() => setFormData({ ...formData, reminders: [...formData.reminders, { type: '' }] })}>
                        <Plus className="w-4 h-4" /> Add Reminder
                      </Button>
                    )}
                    
                    {/* Reminders validation error */}
                    {formErrors.reminders && (
                      <div className="text-red-500 text-xs mt-2">{formErrors.reminders}</div>
                    )}
                </div>
              )}
            </div>
              </div>
              {/* Comment section */}
            <div>
              <Label htmlFor="comment">Comment (Optional)</Label>
              <Textarea
                id="comment"
                placeholder="Add any specific instructions or context for the reviewer..."
                value={formData.comment}
                onChange={(e) => setFormData({...formData, comment: e.target.value})}
                rows={4}
              />
              </div>
            </div>

            {/* Your Email moved to bottom */}
            <div>
              <Label htmlFor="user-email">Your Email *</Label>
              <Input
                id="user-email"
                type="email"
                placeholder="your.email@company.com"
                value={formData.userEmail}
                onChange={(e) => setFormData({...formData, userEmail: e.target.value})}
                required
              />
            </div>

            <div className="flex justify-end flex-col items-end">
              <Button 
                onClick={handleSubmitForm}
                disabled={isUploading || !formData.reviewerEmail.trim() || (formData.deadlineEnabled && (!!formData.deadline !== !!formData.deadlineTime))}
                className="flex items-center gap-2"
              >
                {isUploading && <Loader2 className="animate-spin w-4 h-4" />}
                {isUploading ? 'Creating Review...' : 'Create Review Request'}
              </Button>
              {(!isUploading && !formData.reviewerEmail.trim()) && (
                <span className="text-xs text-red-600 mt-1">Reviewer email is required to create a review request.</span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Step 3: Generated Link
  if (step === 3) {
    return (
      <div className="max-w-2xl mx-auto p-4 md:p-6">
        <Card>
          <CardHeader className="text-center p-4 md:p-6">
            <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 md:w-8 md:h-8 text-green-600" />
            </div>
            <CardTitle className="text-lg md:text-xl">Review Request Created!</CardTitle>
            <CardDescription className="text-sm md:text-base">Your review link has been generated successfully</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 md:space-y-6 p-4 md:p-6">
            <div>
              <Label className="text-sm">Generated Review Link</Label>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 mt-2">
                <Input 
                  value={generatedLink} 
                  readOnly 
                  className="bg-gray-50 text-xs md:text-sm flex-1"
                />
                <div className="flex space-x-2">
                  <Button variant="outline" size="sm" onClick={copyToClipboard} className="flex-1 sm:flex-none">
                    <Copy className="w-4 h-4 mr-1 md:mr-0" />
                    <span className="sm:hidden">Copy</span>
                  </Button>
                  <Button variant="outline" size="sm" onClick={openInNewWindow} className="flex-1 sm:flex-none">
                    <ExternalLink className="w-4 h-4 mr-1 md:mr-0" />
                    <span className="sm:hidden">Open</span>
                  </Button>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 p-3 md:p-4 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2 text-sm md:text-base">What happens next?</h4>
              <ul className="text-xs md:text-sm text-blue-800 space-y-1">
                <li>• Share this link with your reviewer</li>
                <li>• Reviewer can access the content and leave feedback</li>
                <li>• You'll receive notifications about review progress</li>
                {formData.reviewerEmail && <li>• An email has been sent to {formData.reviewerEmail}</li>}
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row justify-between space-y-2 sm:space-y-0 sm:space-x-4">
              <Button variant="outline" onClick={onBack} className="order-2 sm:order-1">
                Back to Dashboard
              </Button>
              <Button onClick={() => setStep(1)} className="order-1 sm:order-2">
                Create Another Review
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }
  return null;
}

const ReviewDashboard: React.FC = () => {
  const [dummyPage,setDummyPage]=useState('dashboard');
  const [reviews,setReviews] = useState<Array<any>>([]);
  const { user } = useUser();
  const { trackPageView, trackButtonClick } = useAnalytics();

  useEffect(()=>{
    // Track page view
    trackPageView('dashboard');
    
    const fetchUserReviews = async () => {
      if(!user) return;
      const { data } = await supabase.from('review_requests').select('*').eq('clerk_user_id', user.id);
      // Quick fix: update status to 'overdue' for open reviews past deadline
      const now = new Date();
      const overdueReviews = (data||[]).filter(r => r.status === 'open' && r.deadline && new Date(r.deadline) < now);
      const overdueIds = overdueReviews.map(r => r.id);
      
      if (overdueIds.length > 0) {
        await supabase.from('review_requests').update({ status: 'overdue' }).in('id', overdueIds);
        
        // Send overdue emails to reviewers
        for (const review of overdueReviews) {
          try {
            // Get reviewer email from review_reviewers table
            const { data: reviewerData } = await supabase
              .from('review_reviewers')
              .select('*')
              .eq('review_request_id', review.id)
              .limit(1)
              .single();
            
            if (reviewerData?.reviewer_email) {
              const baseUrl = window.location.origin;
              const reviewUrl = `${baseUrl}/review/${review.review_link}`;
              
              await EmailService.sendReviewOverdueEmail(reviewerData.reviewer_email, {
                reviewUrl,
                deadline: review.deadline ? new Date(review.deadline).toLocaleString() : undefined,
              });
              
              console.log(`Overdue email sent to ${reviewerData.reviewer_email} for review ${review.review_link}`);
            }
          } catch (emailError) {
            console.error(`Failed to send overdue email for review ${review.id}:`, emailError);
          }
        }
      }
      // Fetch reminders for all reviews
      const reviewIds = (data||[]).map(r => r.id);
      let remindersByReview: Record<string, any[]> = {};
      if (reviewIds.length > 0) {
        const { data: reminders } = await supabase.from('review_reminders').select('*').in('review_request_id', reviewIds);
        if (reminders) {
          remindersByReview = reminders.filter(r => r.sent).reduce((acc, rem) => {
            if (!acc[rem.review_request_id]) acc[rem.review_request_id] = [];
            acc[rem.review_request_id].push(rem);
            return acc;
          }, {} as Record<string, any[]>);
        }
      }
      // fetch reviewer rows for personalised link
      const requestsWithReviewer = await Promise.all((data||[]).map(async (r:any)=>{
        const { data: rev, error } = await supabase.from('review_reviewers').select('*').eq('review_request_id', r.id).limit(1).maybeSingle();
        if (error) {
          console.error('Supabase .eq query error:', error);
        }
        // Attach lastReminderSent
        let lastReminderSent = null;
        if (remindersByReview[r.id] && remindersByReview[r.id].length > 0) {
          lastReminderSent = remindersByReview[r.id].reduce((latest, rem) => {
            return (!latest || new Date(rem.remind_at) > new Date(latest.remind_at)) ? rem : latest;
          }, null);
        }
        return {...r, reviewer_row: rev, lastReminderSent: lastReminderSent ? lastReminderSent.remind_at : null};
      }));
      setReviews(requestsWithReviewer);
    };
    fetchUserReviews();
  },[user]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return (
          <Badge variant="secondary" className="bg-blue-100 text-blue-800 hover:bg-blue-100">
            <Clock className="w-3 h-3 mr-1" />
            Open
          </Badge>
        )
      case 'changes-needed':
        return (
          <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
            <AlertCircle className="w-3 h-3 mr-1" />
            Changes Needed
          </Badge>
        )
      case 'approved':
        return (
          <Badge variant="secondary" className="bg-green-100 text-green-800 hover:bg-green-100">
            <CheckCircle className="w-3 h-3 mr-1" />
            Approved
          </Badge>
        )
      case 'overdue':
        return (
          <Badge variant="secondary" className="bg-red-100 text-red-800 hover:bg-red-100">
            <AlertCircle className="w-3 h-3 mr-1" />
            Overdue
          </Badge>
        )
      default:
        return <Badge variant="secondary">{status}</Badge>
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const now = new Date();
  const thirtyDaysAgo = new Date(now);
  thirtyDaysAgo.setDate(now.getDate() - 30);

  // Helper to parse date
  const parseDate = (d: any) => d ? new Date(d) : null;

  // Only consider reviews created in the last 30 days
  const reviewsLast30Days = reviews.filter(r => {
    const created = parseDate(r.created_at || r.createdAt);
    return created && created >= thirtyDaysAgo;
  });

  // Open reviews in last 30 days
  const openReviews = reviewsLast30Days.filter(r => r.status === 'open');
  // Approved in last 30 days
  const approvedLast30 = reviewsLast30Days.filter(r => r.status === 'approved');
  // Reviews created in last 30 days
  const reviewsCreated = reviewsLast30Days.length;
  // Overdue reviews: count both status 'overdue' and 'open' with deadline < now
  const overdueReviews = reviews.filter(r => {
    if (r.status === 'overdue') return true;
    if (r.status === 'open' && r.deadline) {
      const deadlineDate = new Date(r.deadline);
      return deadlineDate < now;
    }
    return false;
  }).length;
  // Change requests in last 30 days
  const changesNeeded = reviewsLast30Days.filter(r => r.status === 'changes-needed').length;
  // Average time to approval (in days)
  const avgTimeToApproval = (() => {
    const times = approvedLast30
      .map(r => {
        const created = parseDate(r.created_at || r.createdAt);
        const approved = parseDate(r.updated_at || r.updatedAt || r.approved_at || r.deadline); // fallback if no explicit approved date
        if (created && approved && approved > created) {
          return (approved.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
        }
        return null;
      })
      .filter(x => x !== null) as number[];
    if (!times.length) return '—';
    return (times.reduce((a, b) => a + b, 0) / times.length).toFixed(1) + ' days';
  })();

  const analytics = {
    openReviews: openReviews.length,
    approvedLast30: approvedLast30.length,
    averageTimeToApproval: avgTimeToApproval,
    reviewsCreated,
    overdueReviews,
    changesNeeded,
  };

  const navigateTo = useNavigate();
  const location = useLocation();

  const pathMap: Record<string,string> = {
    dashboard: '/',
    start: '/start',
    reviews: '/reviews',
    analytics: '/analytics',
    notifications: '/notifications',
    settings: '/settings'
  };

  const handleMenuClick = (itemId: string) => {
    // If we're in the upload flow (start), handle it specially
    if (dummyPage === 'start') {
      if (itemId === 'dashboard') {
        // Go back to dashboard from upload flow
        setDummyPage('dashboard');
        return;
      }
      // For other menu items, navigate to the actual route
      const target = pathMap[itemId] || '/';
      navigateTo(target);
      setDummyPage(itemId);
      return;
    }

    // Normal navigation when not in upload flow
    const target = pathMap[itemId] || '/';
    if(location.pathname === target) return;

    navigateTo(target);
    setDummyPage(itemId);
  }

  const handleDeleteReviews = async (ids: number[]) => {
    // Track delete action
    await trackButtonClick('delete_reviews', 'dashboard', {
      review_count: ids.length,
      review_ids: ids
    });
    
    // Fetch the review rows to get review_link, id, and file_url
    const { data: reviewsToDelete } = await supabase
      .from('review_requests')
      .select('id, review_link, file_url')
      .in('id', ids);
    if (!reviewsToDelete) return;

    for (const review of reviewsToDelete) {
      await supabase.from('review_annotations').delete().eq('review_link', review.review_link);
      await supabase.from('review_comments').delete().eq('review_link', review.review_link);
      await supabase.from('review_reviewers').delete().eq('review_request_id', review.id);
      if (review.file_url) {
        try {
          const url = new URL(review.file_url);
          const parts = url.pathname.split('/');
          const bucketIndex = parts.indexOf('object') + 1;
          const bucket = parts[bucketIndex];
          const filePath = parts.slice(bucketIndex + 1).join('/');
          if (bucket && filePath) {
            await supabase.storage.from(bucket).remove([filePath]);
          }
        } catch (e) {
          // Optionally log or handle file deletion errors
        }
      }
    }
    await supabase.from('review_requests').delete().in('id', ids);
    setReviews(prev => prev.filter(r => !ids.includes(r.id)));
  };

  const renderCurrentPage = () => {
    if (dummyPage === 'start') {
      return (
        <div className="flex-1 overflow-auto">
          <UploadFlow onBack={() => setDummyPage('dashboard')} />
        </div>
      )
    }

    // Default dashboard content
    return (
      <>
        <header className="border-b border-border bg-background px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Review Dashboard</h1>
              <p className="text-muted-foreground">Manage and track all review requests</p>
            </div>
            <Button 
              className="flex items-center space-x-2"
              onClick={async () => {
                await trackButtonClick('new_request', 'dashboard', {
                  current_review_count: reviews.length
                });
                setDummyPage('start');
              }}
            >
              <Plus className="w-4 h-4" />
              <span>New Request</span>
            </Button>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-6">
          <div className="space-y-6">
            {/* Analytics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Open Reviews</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.openReviews}</div>
                  <p className="text-xs text-muted-foreground">Total open</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Overdue Reviews</CardTitle>
                  <AlertCircle className="h-4 w-4 text-red-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600">{analytics.overdueReviews}</div>
                  <p className="text-xs text-muted-foreground">Total overdue</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Approved Last 30 Days</CardTitle>
                  <CheckCircle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.approvedLast30}</div>
                  <p className="text-xs text-muted-foreground">from last month</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Avg. Time to Approval</CardTitle>
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.averageTimeToApproval}</div>
                  <p className="text-xs text-muted-foreground">from last month</p>
                </CardContent>
              </Card>
            </div>

            <ReviewTable
              reviews={reviews}
              onDelete={handleDeleteReviews}
            />
          </div>
        </main>
      </>
    )
  }

  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleMobileSelect = (id:string)=>{
    handleMenuClick(id);
    setMobileOpen(false);
  }

  return (
    <AppLayout>
      <div className="flex h-screen bg-background md:overflow-hidden">
        {/* mobile top bar */}
        {isMobile && (
          <div className="fixed top-0 left-0 w-full h-12 bg-background border-b flex items-center px-4 z-40 md:hidden">
            <button onClick={()=>setMobileOpen(true)} className="mr-2">
              <Menu className="w-6 h-6" />
            </button>
            <img src={mobileLogo} alt="Approvia Logo" className="h-6 object-contain" />
          </div>
        )}

        {/* sidebar */}
        {!isMobile && <Sidebar activeItem={dummyPage} onMenuClick={handleMenuClick} />}
        {isMobile && (
          <div className={`fixed inset-y-0 left-0 w-64 bg-background border-r z-50 transform ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform md:hidden`}>
            <Sidebar mobile activeItem={dummyPage} onMenuClick={handleMobileSelect} />
          </div>
        )}

        {/* overlay */}
        {isMobile && mobileOpen && <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={()=>setMobileOpen(false)} />}

        {/* main content */}
        <div className={`flex-1 flex flex-col overflow-hidden ${isMobile ? 'pt-12' : ''}`}>
          {renderCurrentPage()}
        </div>
      </div>
    </AppLayout>
  )
}

// In the route for /review/:reviewLink, pass the reviewLink param as a prop to ReviewPage
const ReviewPageWrapper: React.FC = () => {
  const { reviewLink } = useParams<{ reviewLink: string }>();
  return <AppLayout><ReviewPage reviewLink={reviewLink || ''} /></AppLayout>;
};

// simple mobile hook (duplicated from review page for now)
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

const App: React.FC = () => {
  const { isProfileReady, error: profileError } = useUserProfile();
  
  // Initialize Google Analytics
  useAnalytics();

  // Show loading state while user profile is being set up
  if (!isProfileReady) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Setting up your account...</p>
          {profileError && (
            <p className="text-red-500 mt-2">{profileError}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login/*" element={<AppLayout><LoginPage /></AppLayout>} />
      <Route path="/sign-up/*" element={<AppLayout><SignUpPage /></AppLayout>} />
      <Route path="/settings/*" element={<MainShell active="settings"><SettingsPage /></MainShell>} />
      <Route path="/analytics" element={<MainShell active="analytics"><AnalyticsDashboard /></MainShell>} />
      <Route path="/notifications" element={<PlaceholderPage title="Notifications" active="notifications"/>} />
      <Route path="/reviews" element={<PlaceholderPage title="Reviews" active="reviews"/>} />
      <Route path="/review/:reviewLink" element={<ReviewPageWrapper />} />
      <Route path="/subscription-test" element={<MainShell active="settings"><SubscriptionTest /></MainShell>} />
      <Route path="/*" element={<ReviewDashboard />} />
    </Routes>
  );
};

export default App;
